"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import type { Id } from "@agroproj/backend/convex/_generated/dataModel";
import { useMutation, usePaginatedQuery } from "convex/react";
import { ArchiveIcon, ArchiveRestoreIcon, ChevronLeftIcon, MoreHorizontalIcon, PencilIcon, Trash2Icon } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Section } from "@/components/ui/section";
import { metaTextClassName, subsectionTitleClassName } from "@/components/ui/typography";
import { cn } from "@/lib/utils";

/** Saved conversation list shown in place of the chat. Selecting a thread returns to the chat. */
export function AssistantHistory(t0) {
  const $ = _c(56);
  const {
    selected,
    busy,
    onSelect,
    onResumeTemporary
  } = t0;
  const update = useMutation(api.assistant.history.update);
  const remove = useMutation(api.assistant.history.remove);
  const [archived, setArchived] = useState(false);
  let t1;
  if ($[0] !== archived) {
    t1 = {
      archived
    };
    $[0] = archived;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      initialNumItems: 20
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    results,
    status,
    loadMore
  } = usePaginatedQuery(api.assistant.history.list, t1, t2);
  const [rename, setRename] = useState();
  const [title, setTitle] = useState("");
  const [pendingDelete, setPendingDelete] = useState();
  const fail = _temp;
  const renameClosed = useRef(false);
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[3] !== archived || $[4] !== busy || $[5] !== onResumeTemporary || $[6] !== onSelect || $[7] !== rename || $[8] !== results || $[9] !== selected || $[10] !== status || $[11] !== title || $[12] !== update) {
    const finishRename = function finishRename(thread, save) {
      if (renameClosed.current) {
        return;
      }
      renameClosed.current = true;
      setRename(undefined);
      const next = title.trim();
      if (save && next && next !== thread.title) {
        update({
          threadId: thread.id,
          title: next
        }).catch(fail);
      }
    };
    t7 = "flex min-h-0 flex-1 flex-col";
    t3 = "min-h-0 flex-1 overflow-y-auto overscroll-contain p-2";
    if ($[18] !== archived) {
      t4 = archived ? <li className={cn(subsectionTitleClassName, "px-2 py-1.5")}>Arquivadas</li> : null;
      $[18] = archived;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    if ($[20] !== archived || $[21] !== onResumeTemporary) {
      t5 = onResumeTemporary && !archived ? <li><Button aria-current="true" className="w-full justify-start" onClick={onResumeTemporary} size="sm" variant="secondary"><span className="truncate">Conversa temporária</span></Button></li> : null;
      $[20] = archived;
      $[21] = onResumeTemporary;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    t6 = status === "LoadingFirstPage" ? <li className={cn(metaTextClassName, "px-2 py-1.5")}>Carregando...</li> : results.length === 0 ? <li className={cn(metaTextClassName, "px-2 py-1.5")}>{archived ? "Nenhuma conversa arquivada." : "Nenhuma conversa salva."}</li> : results.map(thread_0 => rename === thread_0.id ? <li key={thread_0.id}><form className="px-1 py-1" onSubmit={event => {
        event.preventDefault();
        finishRename(thread_0, true);
      }}><Input aria-label={"T\xEDtulo da conversa"} autoFocus={true} maxLength={100} onBlur={() => finishRename(thread_0, true)} onChange={event_0 => setTitle(event_0.target.value)} onKeyDown={event_1 => {
          if (event_1.key === "Escape") {
            event_1.stopPropagation();
            finishRename(thread_0, false);
          }
        }} value={title} /></form></li> : <li className="flex items-center gap-1" key={thread_0.id}><Button aria-current={selected === thread_0.id ? "true" : undefined} className="min-w-0 flex-1 justify-start" disabled={busy && selected !== thread_0.id} onClick={() => onSelect(thread_0.id)} size="sm" variant={selected === thread_0.id ? "secondary" : "ghost"}><span className="truncate">{thread_0.title}</span></Button><DropdownMenu><DropdownMenuTrigger render={<Button aria-label={`Ações de ${thread_0.title}`} disabled={busy} size="icon-sm" variant="ghost" />}><MoreHorizontalIcon /></DropdownMenuTrigger><DropdownMenuContent align="end"><DropdownMenuItem onClick={() => {
            renameClosed.current = false;
            setRename(thread_0.id);
            setTitle(thread_0.title);
          }}><PencilIcon />Renomear</DropdownMenuItem><DropdownMenuItem onClick={() => {
            update({
              threadId: thread_0.id,
              archived: !thread_0.archived
            }).catch(fail);
          }}>{thread_0.archived ? <ArchiveRestoreIcon /> : <ArchiveIcon />}{thread_0.archived ? "Restaurar" : "Arquivar"}</DropdownMenuItem><DropdownMenuItem onClick={() => setPendingDelete({
            id: thread_0.id,
            title: thread_0.title
          })} variant="destructive"><Trash2Icon />Excluir</DropdownMenuItem></DropdownMenuContent></DropdownMenu></li>);
    $[3] = archived;
    $[4] = busy;
    $[5] = onResumeTemporary;
    $[6] = onSelect;
    $[7] = rename;
    $[8] = results;
    $[9] = selected;
    $[10] = status;
    $[11] = title;
    $[12] = update;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
  }
  let t8;
  if ($[23] !== loadMore || $[24] !== status) {
    t8 = status === "CanLoadMore" ? <li><Button onClick={() => loadMore(20)} size="sm" variant="ghost">Mais conversas</Button></li> : null;
    $[23] = loadMore;
    $[24] = status;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== archived) {
    t9 = () => setArchived(!archived);
    $[26] = archived;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== archived) {
    t10 = archived ? <ChevronLeftIcon /> : <ArchiveIcon />;
    $[28] = archived;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const t11 = archived ? "Voltar \xE0s conversas" : "Ver arquivadas";
  let t12;
  if ($[30] !== archived || $[31] !== t10 || $[32] !== t11 || $[33] !== t9) {
    t12 = <li className="pt-2"><Button aria-pressed={archived} className="text-muted-foreground" onClick={t9} size="sm" variant="ghost">{t10}{t11}</Button></li>;
    $[30] = archived;
    $[31] = t10;
    $[32] = t11;
    $[33] = t9;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t12 || $[36] !== t3 || $[37] !== t4 || $[38] !== t5 || $[39] !== t6 || $[40] !== t8) {
    t13 = <ul className={t3}>{t4}{t5}{t6}{t8}{t12}</ul>;
    $[35] = t12;
    $[36] = t3;
    $[37] = t4;
    $[38] = t5;
    $[39] = t6;
    $[40] = t8;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  const t14 = `"${pendingDelete?.title ?? ""}" será excluída. Esta ação não pode ser desfeita.`;
  let t15;
  if ($[42] !== onSelect || $[43] !== pendingDelete || $[44] !== remove || $[45] !== selected) {
    t15 = async () => {
      if (!pendingDelete) {
        return;
      }
      try {
        await remove({
          threadId: pendingDelete.id
        });
        if (selected === pendingDelete.id) {
          onSelect();
        }
      } catch {
        fail();
      }
    };
    $[42] = onSelect;
    $[43] = pendingDelete;
    $[44] = remove;
    $[45] = selected;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = open => {
      if (!open) {
        setPendingDelete(undefined);
      }
    };
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  const t17 = pendingDelete !== undefined;
  let t18;
  if ($[48] !== t14 || $[49] !== t15 || $[50] !== t17) {
    t18 = <ConfirmationDialog confirmText="Excluir" description={t14} onConfirm={t15} onOpenChange={t16} open={t17} pendingText="Excluindo..." title="Excluir conversa" variant="destructive" />;
    $[48] = t14;
    $[49] = t15;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== t13 || $[53] !== t18 || $[54] !== t7) {
    t19 = <div className={t7}>{t13}{t18}</div>;
    $[52] = t13;
    $[53] = t18;
    $[54] = t7;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  return t19;
}

/** Always available from account settings, even when assistant/history flags are off. */
function _temp() {
  return toast.error("N\xE3o foi poss\xEDvel atualizar o hist\xF3rico.");
}
export function AssistantHistoryPrivacy() {
  const {
    results,
    status,
    loadMore
  } = usePaginatedQuery(api.assistant.history.privacyList, {}, {
    initialNumItems: 20
  });
  const remove = useMutation(api.assistant.history.remove);
  const removeAll = useMutation(api.assistant.history.removeAll);
  const [deleting, setDeleting] = useState(false);
  const [confirmRemoveAll, setConfirmRemoveAll] = useState(false);
  return <Section title="Privacidade das conversas">
      <p>
        Conversas salvas são privadas para sua conta e expiram após 30 dias. A exclusão remove o
        acesso imediatamente; a limpeza ocorre em até 24 horas. Operadores do Convex e backups
        retidos podem ter acesso residual.
      </p>
      <p>Esta lista mostra apenas datas, sem títulos personalizados ou conteúdo.</p>

      <Button className="self-start" size="sm" variant="destructive" disabled={deleting || results.length === 0} onClick={() => setConfirmRemoveAll(true)}>
        Excluir todas as conversas
      </Button>
      <ConfirmationDialog confirmText="Excluir" description="Todas as conversas salvas serão excluídas. Esta ação não pode ser desfeita." onConfirm={async () => {
      setDeleting(true);
      try {
        let more = true;
        while (more) more = (await removeAll({})).more;
        toast.success("Conversas excluídas.");
      } catch {
        toast.error("Não foi possível excluir as conversas.");
      } finally {
        setDeleting(false);
      }
    }} onOpenChange={setConfirmRemoveAll} open={confirmRemoveAll} pendingText="Excluindo..." title="Excluir todas as conversas" variant="destructive" />
      {results.map(thread => <div className="flex items-center justify-between gap-2" key={thread.id}>
          <span>{thread.title}</span>
          <Button size="sm" variant="destructive" onClick={() => {
        void remove({
          threadId: thread.id
        }).catch(() => toast.error("Não foi possível excluir."));
      }}>
            Excluir
          </Button>
        </div>)}
      {status === "CanLoadMore" ? <Button size="sm" variant="ghost" onClick={() => loadMore(20)}>
          Mais conversas
        </Button> : null}
    </Section>;
}